import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { Publicacion } from '../novedades/Publicacion';
import { FormularioDeComunidad } from './FormularioDeComunidad';
import { Miembros } from './Miembros';
import { CompositorDeComunidad, tintaSobre } from './piezas';
import {
  useComunidad, usePublicacionesDeComunidad, useSalir, useUnirse, type Comunidad,
} from './api';

/**
 * DetalleDeComunidad — `#espacios/<id>`. Módulo 9, la opción que carga la deuda.
 *
 * ## Aquí se cierra la última raíz React incrustada del portal
 *
 * Hasta hoy esta pantalla era vanilla y pintaba su muro llamando a
 * `window.PortalReact.montarPublicaciones(...)`: un árbol de React montado
 * dentro de HTML que había escrito otro. Era la única del portal, existía por
 * esta pantalla, y con ella se van `MuroIncrustado.tsx`, las dos funciones del
 * puente y la clase `.feed-composer`.
 *
 * La tarjeta es la misma de Novedades —`Publicacion`— y ahora se usa
 * directamente. No había otra opción sensata: una copia «solo para
 * Comunidades» daría dos versiones de reacciones, comentarios, denuncias,
 * adjuntos y galería divergiendo a la primera corrección.
 *
 * ## Lo que no se pide, y por qué
 *
 * En una comunidad privada de la que no se es miembro, el muro responde 403
 * —salvo a gestión—. La pantalla no lo pide: enseña el aviso de «únete para
 * ver». Pedirlo para pintar el error sería gastar una petición en que el
 * servidor repita lo que la respuesta anterior ya dijo.
 *
 * Hasta el 2026-09-22 esa excepción de gestión solo existía en el servidor: la
 * pantalla le daba los botones de Miembros y Editar, pero no el muro, ni el
 * compositor, ni «Unirme» —que el servidor además le negaba comparando el nombre
 * del rol—.
 */
export function DetalleDeComunidad({ id }: { id: number }) {
  const comunidad = useComunidad(id);
  const c = comunidad.data;

  // La misma regla que `community_posts`: una privada la leen sus miembros y
  // gestión. `can_moderate` es gestión o moderador —y un moderador ya es
  // miembro—. El vanilla dejaba fuera a gestión: tenía los botones de Miembros y
  // Editar, pero para ver el muro le pedía «únete por invitación».
  const puedeVerElMuro = !!c && (c.is_member || !c.is_private || c.can_moderate);
  // Publicar lo permite `_require_member` a los mismos: miembros y gestión.
  const puedePublicar = !!c && (c.is_member || c.can_moderate);
  const publicaciones = usePublicacionesDeComunidad(id, puedeVerElMuro);

  const [editando, setEditando] = useState(false);
  const [viendoMiembros, setViendoMiembros] = useState(false);
  const [confirmandoSalida, setConfirmandoSalida] = useState(false);
  const unirse = useUnirse();
  const salir = useSalir();

  const aviso = (err: unknown, porDefecto: string) =>
    Utils.toast(err instanceof Error ? err.message : porDefecto, 'error');

  if (comunidad.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (comunidad.isError || !c) {
    return (
      <EstadoError
        err={comunidad.error}
        que="la comunidad"
        onReintentar={() => void comunidad.refetch()}
        onVolver={() => APP.navigate('communities')}
      />
    );
  }

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        {/* Enlace y no botón, por lo mismo que las tarjetas: `APP.navigate`
            usa `replaceState` y el Atrás del navegador se saltaría el paso. */}
        <a
          href="#espacios"
          className="inline-flex min-h-11 items-center gap-1.5 rounded-sm px-2 text-3
                     text-ink-2 no-underline hover:text-brand-ink focus-visible:focus-anillo"
        >
          <i className="bi bi-arrow-left" aria-hidden="true" /> Comunidades
        </a>

        <div className="ml-auto flex flex-wrap gap-2">
          {c.can_moderate && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setViendoMiembros(true)}>
                <i className="bi bi-people" aria-hidden="true" /> Miembros
              </Button>
              {/* Con nombre accesible: en el vanilla era un lápiz solo, sin
                  `title` ni `aria-label`, así que un lector de pantalla
                  anunciaba un botón sin nombre. */}
              <Button variant="secondary" size="sm" onClick={() => setEditando(true)}>
                <i className="bi bi-pencil" aria-hidden="true" /> Editar
              </Button>
            </>
          )}
          <AccionDeMembresia
            c={c}
            uniendose={unirse.isPending}
            onUnirse={() =>
              unirse.mutate(id, {
                onSuccess: () => Utils.toast('Te uniste a la comunidad', 'success'),
                onError: (err) => aviso(err, 'No se pudo completar'),
              })
            }
            onSalir={() => setConfirmandoSalida(true)}
          />
        </div>
      </div>

      <Cabecera c={c} />

      {puedePublicar ? (
        <CompositorDeComunidad comunidad={c} />
      ) : (
        <p className="mb-4 flex items-start gap-2 rounded-md border border-line bg-sunken p-3 text-3 text-ink-2">
          <i className="bi bi-info-circle mt-0.5 shrink-0 text-brand-ink" aria-hidden="true" />
          {c.is_private
            ? 'Esta comunidad es privada. Únete por invitación de un moderador para ver y participar.'
            : 'Únete a la comunidad para publicar.'}
        </p>
      )}

      <section aria-label="Publicaciones de la comunidad">
        {!puedeVerElMuro ? null : publicaciones.isLoading ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : publicaciones.isError ? (
          <EstadoError
            err={publicaciones.error}
            que="el muro de la comunidad"
            onReintentar={() => void publicaciones.refetch()}
          />
        ) : !publicaciones.data?.length ? (
          <EstadoVacio
            icono="bi-chat-square"
            titulo="Sin publicaciones"
            descripcion={
              c.is_member
                ? 'Sé el primero en publicar en esta comunidad.'
                : 'Todavía no hay nada publicado aquí.'
            }
            compacto
          />
        ) : (
          <div className="flex flex-col gap-4">
            {publicaciones.data.map((p) => (
              <Publicacion key={p.id} publicacion={p} />
            ))}
          </div>
        )}
      </section>

      {c.can_moderate && (
        <>
          <FormularioDeComunidad
            abierto={editando}
            comunidad={c}
            onCerrar={() => setEditando(false)}
          />
          <Miembros
            abierto={viendoMiembros}
            comunidad={c}
            onCerrar={() => setViendoMiembros(false)}
          />
        </>
      )}

      <ConfirmDialog
        abierto={confirmandoSalida}
        onCambio={(v) => { if (!v) setConfirmandoSalida(false); }}
        titulo="Salir de la comunidad"
        descripcion={`Dejarás de ver las publicaciones de «${c.name}».${
          c.is_private ? ' Al ser privada, volver a entrar necesita invitación de un moderador.' : ''
        }`}
        textoConfirmar="Salir"
        destructivo
        cargando={salir.isPending}
        onConfirmar={() => {
          // Quien sale de una privada sin ser de gestión ya no puede verla:
          // volver a pedirla daría un 403 y un error justo después del éxito.
          const seVa = c.is_private && !c.can_moderate;
          salir.mutate({ id, seVa }, {
            onSuccess: () => {
              setConfirmandoSalida(false);
              Utils.toast('Saliste de la comunidad', 'info');
              if (seVa) location.hash = '#espacios';
            },
            onError: (err) => {
              setConfirmandoSalida(false);
              aviso(err, 'No se pudo salir de la comunidad');
            },
          });
        }}
      />
    </>
  );
}

/* ── Piezas de esta pantalla ───────────────────────────────────────────── */

/**
 * Unirse, salir, o nada.
 *
 * Un moderador **no** ve «Salir», y no es un olvido heredado del vanilla: si se
 * va, la comunidad puede quedarse sin quien la modere. Quitarse el rol es cosa
 * de la pantalla de Miembros, que es donde se ve quién más queda.
 */
function AccionDeMembresia({
  c, uniendose, onUnirse, onSalir,
}: {
  c: Comunidad;
  uniendose: boolean;
  onUnirse: () => void;
  onSalir: () => void;
}) {
  if (!c.is_member) {
    // Una privada se une por invitación; gestión puede unirse sola, como
    // permite `join_community`.
    if (c.is_private && !c.can_moderate) return null;
    return (
      <Button size="sm" loading={uniendose} onClick={onUnirse}>
        <i className="bi bi-plus-lg" aria-hidden="true" /> Unirme
      </Button>
    );
  }
  if (c.my_role === 'moderator') return null;
  return (
    <Button variant="ghost" size="sm" onClick={onSalir}>
      <i className="bi bi-box-arrow-right" aria-hidden="true" /> Salir
    </Button>
  );
}

function Cabecera({ c }: { c: Comunidad }) {
  const tinta = tintaSobre(c.color);

  return (
    <div className="mb-5 overflow-hidden rounded-md border border-line bg-surface">
      <div
        className="flex items-center gap-4 p-4"
        style={{ background: c.color, color: tinta.texto }}
      >
        <i className={cn('bi shrink-0 text-9 leading-none', c.icon || 'bi-people')} aria-hidden="true" />
        <div className="min-w-0">
          <h1 className="text-6 font-bold leading-tight [overflow-wrap:anywhere]">{c.name}</h1>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-2" style={{ color: tinta.suave }}>
            <span className="flex items-center gap-1.5">
              <i className="bi bi-people-fill" aria-hidden="true" />
              {c.member_count} {c.member_count === 1 ? 'miembro' : 'miembros'}
            </span>
            {c.area_name && <span>· {c.area_name}</span>}
            {c.is_private && (
              <span className="flex items-center gap-1">
                · <i className="bi bi-lock-fill" aria-hidden="true" /> Privada
              </span>
            )}
          </p>
        </div>
      </div>

      {(c.description || c.rules) && (
        <div className="flex flex-col gap-3 p-4">
          {c.description && (
            <p className="text-3 text-ink-2 [overflow-wrap:anywhere]">{c.description}</p>
          )}
          {c.rules && (
            // Superficie plana con filete de 1 px, la decisión del 2026-09-01:
            // el tono y la línea separan, no un filete de color en un borde.
            <div className="rounded-sm border border-line bg-sunken px-3 py-2.5">
              <h2 className="text-1 font-bold uppercase tracking-wide text-muted">
                Reglas de convivencia
              </h2>
              <p className="mt-1 whitespace-pre-line text-3 text-ink-2 [overflow-wrap:anywhere]">
                {c.rules}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
